import { isWellId } from '@core/core/helpers'
import {
  addCorrelationSchemeWells,
  getCorrelationSchemeWellsIds,
  selectAllCorrelationSchemeWellsIds,
  useAppDispatch,
  useAppSelector,
} from '@core/core/store'
import type { ActiveEntity } from '@core/core/types'
import type { ProjectId, TabId, WellId } from 'common/types'
import { useEffect } from 'react'

type CorrelationSchemeResult = {
  sortedEntities: ActiveEntity[]
}

export const useCorrelationSchemeOrder = (
  entities: ActiveEntity[],
  tabId: TabId,
  projectId: ProjectId,
): CorrelationSchemeResult => {
  const dispatch = useAppDispatch()
  const correlationSchemesWellsOrder = useAppSelector(selectAllCorrelationSchemeWellsIds)

  useEffect(() => {
    dispatch(getCorrelationSchemeWellsIds({ projectId, actualTabId: tabId }))
  }, [projectId, tabId])

  useEffect(() => {
    const order = correlationSchemesWellsOrder.find(({ id }) => id === tabId)
    if (!order) return

    const wellIds = entities.reduce<WellId[]>((arr, { entityType, entityId }) => {
      if (isWellId(entityId, entityType)) {
        arr.push(entityId)
      }
      return arr
    }, [])

    const currentCorrelationSchemeWellOrder = order.wellsIdsList

    if (currentCorrelationSchemeWellOrder.length < wellIds.length) {
      const newWellsToAddInOrder = wellIds.filter(
        well => !currentCorrelationSchemeWellOrder.includes(well),
      )
      if (newWellsToAddInOrder.length) {
        const newWellsIdsList = [...currentCorrelationSchemeWellOrder, ...newWellsToAddInOrder]
        dispatch(addCorrelationSchemeWells({ projectId, actualTabId: tabId, newWellsIdsList }))
      }
    }

    if (currentCorrelationSchemeWellOrder.length > wellIds.length) {
      const wellsToRemoveFromOrder = currentCorrelationSchemeWellOrder.filter(
        well => !wellIds.includes(well),
      )
      if (wellsToRemoveFromOrder.length) {
        const newWellsIdsList = [
          ...currentCorrelationSchemeWellOrder.filter(
            well => !wellsToRemoveFromOrder.includes(well),
          ),
        ]
        dispatch(addCorrelationSchemeWells({ projectId, actualTabId: tabId, newWellsIdsList }))
      }
    }
  }, [correlationSchemesWellsOrder, entities, tabId, projectId])

  const currentCorrelationSchemeWellIds = correlationSchemesWellsOrder.find(
    ({ id }) => id === tabId,
  )?.wellsIdsList

  if (!currentCorrelationSchemeWellIds) return { sortedEntities: entities }

  const sortedEntities = entities.slice().sort((e1, e2) => {
    const aId: string = e1.entityId ?? ''
    const bId: string = e2.entityId ?? ''
    return (
      currentCorrelationSchemeWellIds.indexOf(aId) - currentCorrelationSchemeWellIds.indexOf(bId)
    )
  })

  return { sortedEntities }
}
